import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAccount, useChainId, useSwitchChain, useWriteContract, useWaitForTransactionReceipt } from 'wagmi';
import ErrorModal from './ErrorModal';
import { useWalletPortfolio } from '../context/WalletPortfolioContext';

const GLOBAL_CLAIMS_COLLAPSED_KEY = 'globalClaimsCollapsed';
const POLYGON_CHAIN_ID = 137;
const MARKET_ABI = [
  {
    name: 'claim',
    type: 'function',
    stateMutability: 'nonpayable',
    inputs: [],
    outputs: []
  },
  {
    name: 'claimRefund',
    type: 'function',
    stateMutability: 'nonpayable',
    inputs: [],
    outputs: []
  }
];

const MARKET_ADDR_RE = /^0x[a-fA-F0-9]{40}$/;

const marketTitleLinkStyle = {
  flex: 1,
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  color: '#0d47a1',
  fontWeight: 600,
  textDecoration: 'underline',
  textUnderlineOffset: '2px'
};

function MarketTitleLink({ address, homeTeam, awayTeam }) {
  const label = `${homeTeam || '—'} vs ${awayTeam || '—'}`;
  if (!address || !MARKET_ADDR_RE.test(address)) {
    return <span style={{ ...marketTitleLinkStyle, color: 'inherit', textDecoration: 'none', fontWeight: 'inherit' }}>{label}</span>;
  }
  return (
    <Link
      to={`/market/${address}`}
      style={marketTitleLinkStyle}
      title={`Open market ${address.slice(0, 10)}…`}
    >
      {label}
    </Link>
  );
}

function GlobalClaims({ onClaimSuccess }) {
  const { address, isConnected } = useAccount();
  const chainId = useChainId();
  const { switchChain } = useSwitchChain();
  const {
    claimableList: claimableMarkets,
    activeTrades,
    loading,
    hasLoadedOnce,
    refreshPortfolio,
  } = useWalletPortfolio();
  const [showSuccess, setShowSuccess] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [claimingMarket, setClaimingMarket] = useState(null);
  const [claimIntent, setClaimIntent] = useState(null);
  const [errorModal, setErrorModal] = useState({ isOpen: false, title: '', message: '', type: 'error' });

  // Write contract for per-market claims
  const { writeContract: claimMarket, data: claimHash, error: writeError } = useWriteContract();

  // Wait for transaction
  const { isLoading: isClaiming, isSuccess: claimSuccess, error: txError } = useWaitForTransactionReceipt({
    hash: claimHash,
  });

  // Restore collapse state only once per browser.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const stored = window.localStorage.getItem(GLOBAL_CLAIMS_COLLAPSED_KEY);
      setIsCollapsed(stored === 'true');
    } catch (_) {}
  }, []);

  // Handle success
  useEffect(() => {
    if (claimSuccess && claimHash) {
      if (typeof onClaimSuccess === 'function') {
        // Refresh header balances immediately when claim tx is confirmed.
        Promise.resolve(onClaimSuccess()).catch(() => {});
        // Follow-up refresh to handle occasional RPC/provider lag.
        setTimeout(() => {
          Promise.resolve(onClaimSuccess()).catch(() => {});
        }, 1500);
      }

      setErrorModal({
        isOpen: true,
        title: 'Claim Successful',
        message: `Your ${claimIntent?.type === 'refund' ? 'refund' : 'winnings'} were claimed successfully.\n\nTransaction: ${claimHash.slice(0, 10)}...${claimHash.slice(-8)}\n\nYour USDC balance will update automatically.`,
        type: 'success',
        linkUrl: `https://polygonscan.com/tx/${claimHash}`,
        linkLabel: 'View this transaction on PolygonScan'
      });
      
      setShowSuccess(true);
      setClaimingMarket(null);
      setClaimIntent(null);
      
      setTimeout(() => {
        setShowSuccess(false);
        void refreshPortfolio();
      }, 3000);
      
    }
  }, [claimSuccess, claimHash, claimIntent, refreshPortfolio, onClaimSuccess]);

  // Handle errors
  useEffect(() => {
    if (writeError) {
      console.error('Write contract error:', writeError);
      setErrorModal({
        isOpen: true,
        title: 'Transaction Failed',
        message: writeError.message || 'Failed to submit claim transaction. Please try again.',
        type: 'error'
      });
      setClaimingMarket(null);
      setClaimIntent(null);
    }
  }, [writeError]);

  useEffect(() => {
    if (txError) {
      console.error('Transaction error:', txError);
      setErrorModal({
        isOpen: true,
        title: 'Transaction Failed',
        message: txError.message || 'Transaction was rejected or failed. Please try again.',
        type: 'error'
      });
      setClaimingMarket(null);
      setClaimIntent(null);
    }
  }, [txError]);

  const handleClaimMarket = (market) => {
    if (!isConnected) {
      setErrorModal({
        isOpen: true,
        title: 'Wallet Not Connected',
        message: 'Please connect your wallet first',
        type: 'warning'
      });
      return;
    }

    if (chainId !== POLYGON_CHAIN_ID) {
      setErrorModal({
        isOpen: true,
        title: 'Wrong Network',
        message: 'Please switch your wallet to the Polygon network to claim.',
        type: 'warning'
      });
      try {
        switchChain?.({ chainId: POLYGON_CHAIN_ID });
      } catch (_) {
        /* user can switch manually */
      }
      return;
    }

    if (!market?.address) {
      setErrorModal({
        isOpen: true,
        title: 'Invalid Market',
        message: 'Market address not found for this claim.',
        type: 'error'
      });
      return;
    }

    try {
      const claimType = market.type === 'refund' ? 'refund' : 'winnings';
      const functionName = claimType === 'refund' ? 'claimRefund' : 'claim';
      setClaimingMarket(market.address.toLowerCase());
      setClaimIntent({ address: market.address, type: claimType });

      claimMarket({
        address: market.address,
        abi: MARKET_ABI,
        functionName
      });
    } catch (error) {
      console.error('Error claiming:', error);
      setErrorModal({
        isOpen: true,
        title: 'Claim Failed',
        message: error.message || 'An unexpected error occurred. Please check console for details.',
        type: 'error'
      });
      setClaimingMarket(null);
      setClaimIntent(null);
    }
  };

  const setCollapsed = (nextValue) => {
    setIsCollapsed(nextValue);
    if (typeof window !== 'undefined') {
      try {
        window.localStorage.setItem(GLOBAL_CLAIMS_COLLAPSED_KEY, String(nextValue));
      } catch (_) {}
    }
  };

  if (!isConnected) {
    return null;
  }

  if (!hasLoadedOnce && loading) {
    return null;
  }

  const totalItems = claimableMarkets.length + activeTrades.length;

  // Collapsed view
  if (isCollapsed) {
    return (
      <div style={{
        position: 'fixed',
        bottom: '20px',
        left: '20px',
        background: 'linear-gradient(135deg, #ffd700 0%, #ffed4e 100%)',
        padding: '12px 16px',
        borderRadius: '12px',
        boxShadow: '0 8px 32px rgba(255, 215, 0, 0.4)',
        color: '#000',
        zIndex: 1000,
        border: '2px solid rgba(0, 0, 0, 0.2)',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        cursor: 'pointer',
        transition: 'all 0.2s'
      }}
      onClick={() => setCollapsed(false)}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'scale(1.02)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'scale(1)';
      }}
      >
        <div>
          <div style={{ fontSize: '14px', fontWeight: 'bold' }}>
            {totalItems} Market{totalItems !== 1 ? 's' : ''}
          </div>
          <div style={{ fontSize: '10px', opacity: 0.8 }}>
            Active trades & claims
          </div>
        </div>
        <div style={{ 
          fontSize: '18px',
          fontWeight: 'bold',
          color: '#000',
          marginLeft: 'auto'
        }}>
          +
        </div>
      </div>
    );
  }

  // Expanded view
  return (
    <div style={{
      position: 'fixed',
      bottom: '20px',
      left: '20px',
      background: 'linear-gradient(135deg, #ffd700 0%, #ffed4e 100%)',
      padding: '20px',
      borderRadius: '12px',
      boxShadow: '0 8px 32px rgba(255, 215, 0, 0.4)',
      color: '#000',
      minWidth: '280px',
      maxWidth: '320px',
      zIndex: 1000,
      border: '2px solid rgba(0, 0, 0, 0.2)'
    }}>
      {showSuccess && (
        <div style={{
          position: 'absolute',
          top: '-10px',
          left: '50%',
          transform: 'translateX(-50%)',
          background: '#4caf50',
          padding: '8px 16px',
          borderRadius: '20px',
          fontSize: '12px',
          fontWeight: 'bold',
          color: 'white',
          boxShadow: '0 4px 12px rgba(76, 175, 80, 0.4)'
        }}>
          Claims Updated
        </div>
      )}

      {/* Collapse button */}
      <button
        onClick={() => setCollapsed(true)}
        style={{
          position: 'absolute',
          top: '8px',
          right: '8px',
          background: 'rgba(0, 0, 0, 0.1)',
          border: 'none',
          color: '#000',
          width: '24px',
          height: '24px',
          borderRadius: '50%',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '18px',
          fontWeight: 'bold',
          transition: 'all 0.2s'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'rgba(0, 0, 0, 0.2)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'rgba(0, 0, 0, 0.1)';
        }}
      >
        −
      </button>

      <div style={{ fontSize: '14px', opacity: 0.9, marginBottom: '8px', fontWeight: '600' }}>
        Your Markets
      </div>

      <div style={{ 
        fontSize: '32px', 
        fontWeight: 'bold', 
        marginBottom: '4px',
        textShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        {totalItems}
      </div>

      <div style={{ fontSize: '11px', opacity: 0.8, marginBottom: '16px' }}>
        {totalItems === 1 ? 'market in your account' : 'markets in your account'}
      </div>

      {/* Active trades (open/closed unresolved markets) */}
      {activeTrades.length > 0 && (
        <>
          <div style={{ fontSize: '11px', fontWeight: '700', marginBottom: '6px' }}>
            Active Trades
          </div>
          <div style={{
            maxHeight: '150px',
            overflowY: 'auto',
            background: 'rgba(0, 0, 0, 0.05)',
            borderRadius: '8px',
            padding: '8px',
            marginBottom: '10px'
          }}>
            {activeTrades.map((trade) => (
              <div key={`trade-${trade.marketAddress}`} style={{
                fontSize: '11px',
                padding: '8px',
                marginBottom: '4px',
                background: 'rgba(255, 255, 255, 0.5)',
                borderRadius: '6px'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', alignItems: 'center' }}>
                  <MarketTitleLink
                    address={trade.marketAddress}
                    homeTeam={trade.homeTeam}
                    awayTeam={trade.awayTeam}
                  />
                  <div style={{ fontSize: '10px', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                    {trade.totalTradeSize.toFixed(2)} USDC
                  </div>
                </div>
                <div style={{ marginTop: '4px', fontSize: '10px', opacity: 0.75 }}>
                  {trade.outcomeLabel && (
                    <span style={{ fontWeight: 700, marginRight: '6px' }}>{trade.outcomeLabel}</span>
                  )}
                  Status: {trade.marketState}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Claimable markets */}
      {claimableMarkets.length > 0 && (
        <div style={{ fontSize: '11px', fontWeight: '700', marginBottom: '6px' }}>
          Claims
        </div>
      )}
      <div style={{ 
        maxHeight: '260px',
        overflowY: 'auto',
        background: 'rgba(0, 0, 0, 0.05)',
        borderRadius: '8px',
        padding: '8px'
      }}>
        {claimableMarkets.length === 0 && activeTrades.length === 0 && (
          <div style={{ fontSize: '11px', opacity: 0.8, textAlign: 'center', padding: '8px 4px' }}>
            No active trades or claims yet.
          </div>
        )}
        {claimableMarkets.map((market, idx) => {
          const isThisClaim = claimingMarket === market.address.toLowerCase();
          return (
          <div key={market.address} style={{
            fontSize: '11px',
            padding: '8px',
            marginBottom: idx < claimableMarkets.length - 1 ? '4px' : '0',
            background: 'rgba(255, 255, 255, 0.5)',
            borderRadius: '6px',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', alignItems: 'center' }}>
              <MarketTitleLink
                address={market.address}
                homeTeam={market.homeTeam}
                awayTeam={market.awayTeam}
              />
              <div style={{ fontSize: '10px', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                {parseFloat(market.claimableAmount || 0).toFixed(2)} USDC
              </div>
            </div>
            <button
              onClick={() => handleClaimMarket(market)}
              disabled={isClaiming}
              style={{
                width: '100%',
                padding: '8px 10px',
                background: isThisClaim
                  ? 'rgba(0, 0, 0, 0.2)'
                  : 'linear-gradient(135deg, #4caf50 0%, #45a049 100%)',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                fontSize: '11px',
                fontWeight: 'bold',
                cursor: isClaiming ? 'not-allowed' : 'pointer',
                opacity: isClaiming ? 0.7 : 1
              }}
            >
              {isThisClaim && isClaiming
                ? 'Claiming...'
                : `Claim ${parseFloat(market.claimableAmount || 0).toFixed(2)} USDC`}
            </button>
          </div>
          );
        })}
      </div>

      <div style={{ 
        fontSize: '10px', 
        opacity: 0.8, 
        marginTop: '8px',
        textAlign: 'center',
        color: '#000'
      }}>
        Visible at all times unless minimized
      </div>

      {/* Error Modal */}
      <ErrorModal
        isOpen={errorModal.isOpen}
        onClose={() => setErrorModal({ ...errorModal, isOpen: false })}
        title={errorModal.title}
        message={errorModal.message}
        type={errorModal.type}
        linkUrl={errorModal.linkUrl}
        linkLabel={errorModal.linkLabel}
      />
    </div>
  );
}

export default GlobalClaims;

